import React from 'react';

import Section from 'Elements/Section';
import { H2 } from 'Elements/Typography';
import Main from 'Elements/Main';

import Grid, { Col } from 'Components/Grid';
import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import Video, { VideoProps } from 'Components/Video';
import Accordion, { AccordionProps } from 'Components/Accordion';
import Block from 'Components/Block';
import LineList, { LineListProps } from 'Components/List/LineList';
import Carousel, { CarouselProps } from 'Components/Carousel';

import Image, { HeroBannerImageProps } from 'Modules/HeroBanner/MakeWaves/Image';
import IndustryLinks, { IndustryLinksProps } from 'Modules/IndustryLinks';
import SolentStory, { SolentStoryPromopointProps } from 'Modules/PromoPoint/SolentStory';
import Facilities, { FacilitiesMiniListingProps } from 'Modules/MiniListing/Facilities';
import General, { GeneralPromoPointProps } from 'Modules/PromoPoint/General';
import Brand, { BrandBannerProps } from 'Modules/Banner/Brand';
import Ben, { BenMiniListingProps } from 'Modules/MiniListing/Ben';
import Course, { CourseMiniListingProps } from 'Modules/MiniListing/Course';

import { LineListWrapper } from './Subject.styled';

export interface Props {
    heroBanner: HeroBannerImageProps;
    breadcrumbs: BreadcrumbsProps;
    introduction: {
        text: React.ReactNode;
        video?: VideoProps;
        carousel?: CarouselProps;
        listHeading: { heading: string; colour: string };
        list?: LineListProps;
        accreditations?: {
            heading?: string;
            backgroundColour?: string;
            colour?: string;
            style?: React.CSSProperties;
            children: React.ReactElement | number | string;
        };
    };
    courseMiniListing: CourseMiniListingProps;
    industryLinks?: IndustryLinksProps;
    solentStories?: SolentStoryPromopointProps;
    facilities?: FacilitiesMiniListingProps;
    generalPromo: GeneralPromoPointProps;
    news?: BenMiniListingProps;
    faq: {
        heading: string;
        accordions: AccordionProps[];
    };
    brandPromo: BrandBannerProps;
}

const Subject: React.FC<Props> = props => {
    const {
        heroBanner,
        breadcrumbs,
        introduction,
        courseMiniListing,
        industryLinks,
        solentStories,
        facilities,
        generalPromo,
        news,
        faq,
        brandPromo
    } = props;

    return (
        <Main>
            <Breadcrumbs {...breadcrumbs} />
            <Image {...heroBanner} />
            <Section id="subject_introduction" paddingTop={1}>
                <Grid>
                    <Col b={12} m={[1, 10]} l={[1, 8]}>
                        {introduction.text}
                        {(introduction.video || introduction.carousel) && (
                            <div style={{ paddingTop: '2rem' }}>
                                {introduction.video ? (
                                    <Video {...introduction.video} />
                                ) : (
                                    introduction.carousel && <Carousel {...introduction.carousel} />
                                )}
                            </div>
                        )}
                        <div style={{ paddingTop: '2rem' }}>
                            <H2
                                color={introduction.listHeading.colour}
                                size={{ small: '1.5rem', large: '2rem' }}
                            >
                                {introduction.listHeading.heading}
                            </H2>
                        </div>
                        {introduction.list && (
                            <LineListWrapper>
                                <div style={{ padding: '2rem' }}>
                                    <LineList {...introduction.list} />
                                </div>
                            </LineListWrapper>
                        )}
                    </Col>
                    {introduction.accreditations && (
                        <Col b={12} m={[1, 10]} l={[9, 13]}>
                            <Block {...introduction.accreditations} />
                        </Col>
                    )}
                </Grid>
            </Section>
            <Course {...courseMiniListing} />
            {industryLinks && (
                <Section id="subject_industry_links" paddingTop={1}>
                    <IndustryLinks {...industryLinks} />
                </Section>
            )}
            {solentStories && <SolentStory {...solentStories} />}
            {facilities && <Facilities {...facilities} />}
            <Section id="subject_open_day_promo" paddingBottom={0}>
                <General {...generalPromo} />
            </Section>
            {news && <Ben {...news} />}
            <Section id="subject_faq" paddingTop={1}>
                <Grid>
                    <Col b={12} m={[1, 10]}>
                        <H2 size={{ small: '1.5rem', large: '2rem' }}>{faq.heading}</H2>
                        <div>
                            {faq.accordions.map((item, index) => (
                                <Accordion {...item} key={index} />
                            ))}
                        </div>
                    </Col>
                </Grid>
            </Section>
            <Section id="subject_brand_promo" paddingBottom={0}>
                <Brand {...brandPromo} />
            </Section>
            {/* <CourseCompare /> */}
        </Main>
    );
};

export default Subject;
